import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import cx from 'classnames';
import { renderToStaticMarkup } from 'react-dom/server';
import translate from '../../../commonComponents/translate';
import withCSRF from '../../../commonComponents/withCSRF';
import { compose } from '../../../services/utilityFunctions';
import RenderQuestionsPoll from './survey/RenderQuestionsPoll';
import Loader from '../../../services/loader';
import Notifications from '../../../services/notifications';
import List from '../../../commonComponents/lists/List';
import UserColumn from '../../../commonComponents/lists/columns/UserColumn';
import LoggedUserContext from '../../../contextProviders/LoggedUserContext';
import SurveyJSAnalytics from '../../../commonComponents/surveyJS/SurveyJSAnalytics';

function SurveyDisplay({ t, _csrf, lesson, sessionId, onLessonDone, sessionTrainer }) {
  const isExternalProvider = lesson?.survey?.providerType === 'surveyjs';
  const tableEl = useRef(null);
  /** STATES */
  // Check if exist surveyContext
  const [existSurveyContext, setExistSurveyContext] = useState(lesson?.surveyContext);
  // Response from stats
  const [questionsStats, setQuestionsStats] = useState([]);
  const [surveyJSPoll, setSurveyJSPoll] = useState([]);
  // Question selected (index)
  const [questionSelected, setQuestionSelected] = useState(0);

  // handle survey in session course or not
  const { loggedUser } = useContext(LoggedUserContext);
  // Make sure that the user accessing the session to have access to the survey is the session trainer
  const isTheSessionTrainer = loggedUser._id === sessionTrainer;
  const buttonDisabled = lesson && sessionId && isTheSessionTrainer;
  useEffect(() => {
    if (lesson?.status === 'unlocked' && !sessionId && !isTheSessionTrainer) {
      onLessonDone(); // Mark lesson as done
    }
  }, []);

  /** LOADER */
  // Loader for changing question
  const loader = useRef(new Loader('#questions-loader'));
  const [isLoading, setLoading] = useState(false);
  useEffect(
    () => {
      if (loader.current) {
        if (isLoading) {
          loader.current.show();
        } else {
          loader.current.hide();
        }
      }
    },
    [isLoading],
  );

  useEffect(() => {
    setExistSurveyContext(lesson?.surveyContext);
  }, [lesson]);

  /** API REQUESTS */
  // Send survey to participants
  const sendSurvey = () => {
    setLoading(true);
    const url = `/api/access-session/${sessionId}/course/lesson/${lesson._id}`;
    axios.post(url, null,
      {
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(() => {
        setExistSurveyContext(true);
        setLoading(false);
        onLessonDone(sessionId);
      })
      .catch(err => {
        console.log(err);
        Notifications.showErrorFromRequest(t(err), 'ERROR_SESSION_NO_PARTICIPANTS');
        setLoading(false);
      });
  };

  // Get Survey Stats/Answers
  const getSurveyAnswers = () => {
    setLoading(true);
    if (tableEl && tableEl.current) {
      tableEl.current.forceReload();
    }
    const surveyFromLesson = lesson?.surveyContext?.surveyContext;
    if (surveyFromLesson) {
      const params = {
        surveyContext: surveyFromLesson,
      };
      if (!isExternalProvider) {
        axios.get(
          `/api/survey/${lesson.survey._id}/stats`,
          {
            headers: {
              'Accept-Version': '1.0.0',
              'X-Gamification-API': 'Games',
            },
            params,
          })
          .then(({ data: response }) => {
            setQuestionsStats(response.data);
            setLoading(false);
          })
          .catch(err => {
            console.log(err);
            setLoading(false);
          });
      } else {
        axios.get(
          `/api/survey/externalProvider/${lesson.survey._id}/answers`,
          {
            headers: {
              'Accept-Version': '1.0.0',
              'X-Gamification-API': 'Games',
            },
            params,
          })
          .then(({ data: response }) => {
            const surveyJSContainer = document.getElementById('surveyVizPanel');
            if (surveyJSContainer) {
              while (surveyJSContainer.firstChild) {
                surveyJSContainer.removeChild(surveyJSContainer.lastChild);
              }
            }
            setSurveyJSPoll(response.data);
            setLoading(false);
          })
          .catch(err => {
            console.log(err);
            setLoading(false);
          });
      }
    } else {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (existSurveyContext) {
      getSurveyAnswers();
    }
  }, [lesson, existSurveyContext]);

  // Change Question shown
  const changeQuestion = (direction, index) => {
    // ARROWS
    if (direction !== 'none') {
      const isBack = direction === 'back';
      const nextIndex = isBack ? index - 1 : index + 1;
      setLoading(true);
      // console.log('*TYPE:', isBack, nextIndex,
      //   questionsStats.questions[index]?.question?.type,
      //   questionsStats.questions[nextIndex]?.question?.type);

      setQuestionSelected(nextIndex);

      if (questionsStats.questions[index]?.question?.type === 'opinion'
        && questionsStats.questions[nextIndex]?.question?.type === 'opinion') {
        if (tableEl && tableEl.current) {
          tableEl.current.destroy();
        }
        if (tableEl && tableEl.current) {
          tableEl.current.componentDidMount();
        }
      }
      setLoading(false);
    } else {
      // QUESTIONS SELECTOR
      setQuestionSelected(index);
      if (questionsStats.questions[index]?.question?.type === 'opinion') {
        if (tableEl && tableEl.current) {
          tableEl.current.destroy();
        }
        if (tableEl && tableEl.current) {
          tableEl.current.componentDidMount();
        }
      }
    }
  };

  // Render question differently if bravon or surveyjs
  const renderQuestionTitle = (type, q) => {
    let title;
    if (type === 'bravon') {
      title = q?.question?.question || '';
    } else {
      title = q?.question?.name || '';
    }
    return (
      <span>{title}</span>
    );
  };

  // Class for number of questions
  const indicatorClass = index =>
    cx('survey-question-indicator', {
      'status-correct': index === questionSelected,
    });
  return (
    <div id="surveyWrapper">
      {!sessionId && (
        <div className="course-not-inSession">
          <h3>{t('survey.lesson.notInSession.warning')}</h3>
          <h5>{t('survey.lesson.notInSession.hint')}</h5>
        </div>
      )}
      {sessionId && (
        <div>
          {!existSurveyContext && (
            <button
              type="button"
              className="btn btn-dynamic"
              onClick={sendSurvey}
              disabled={!buttonDisabled}
            >
              {t('course.lesson.survey.send')}
            </button>
          )}
          <div id="questions-loader" className="row align-questions">
          {existSurveyContext && (
            <div className="col-12 btn-reload-questions">
              <button
                type="button"
                className={`btn btn-dynamic ${isLoading ? ' m-loader m-loader--right m-loader--light' : ''}`}
                onClick={() => getSurveyAnswers()}
              >
                {t('reload')}
              </button>
            </div>
          )}
          {!isExternalProvider && existSurveyContext && questionsStats?.questions?.length > 0 && (
            <div className="row academy-survey-questions">
              {questionsStats.questions.map((q, index) => {
                if (index === questionSelected) {
                  return (
                    <div className="col-12">
                      <div className="row survey-display-container">
                        <div className="col-1 survey-display-column">
                          {index > 0 && (
                            <button
                              type="button"
                              className="btn-previous-question"
                              onClick={() => changeQuestion('back', index)}
                            >
                              <i className="la la-angle-left" />
                            </button>
                          )}
                        </div>
                        <div className="col-lg-10 col-8 survey-display-column">
                          <div className="survey-wrapper">
                            <div className="survey-container">
                              <div className="survey-question-question">
                                {renderQuestionTitle(questionsStats?.providerType || 'bravon', q)}
                              </div>
                              <div className="survey-question-indicator-container">
                                {questionsStats.questions.map((question, i) => (
                                  // eslint-disable-next-line jsx-a11y/control-has-associated-label
                                  <button
                                    type="button"
                                    key={question._id}
                                    className={indicatorClass(i)}
                                    onClick={() => changeQuestion('none', i)}
                                  />
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-1 survey-display-column">
                          {index < questionsStats.questions.length - 1 && (
                            <button
                              type="button"
                              className="btn-next-question"
                              onClick={() => changeQuestion('next', index)}
                            >
                              <i className="la la-angle-right" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }
                return (<></>);
              })}
              {questionsStats?.questions[questionSelected]?.question?.type !== 'opinion' && (
                <div className="col-12">
                  <div className="row">
                    <RenderQuestionsPoll
                      t={t}
                      questionStats={questionsStats.questions[questionSelected]}
                      surveyContext={lesson?.surveyContext?.surveyContext}
                      ref={tableEl}
                    />
                  </div>
                </div>
              )}
              {questionsStats?.questions[questionSelected]?.question?.type === 'opinion' && (
                <div className="col-12 poll-answers">
                  <div id="OpinionContainer" className="list-opinion-container">
                    <List
                      ref={tableEl}
                      columns={[
                        {
                          field: 'user',
                          width: 150,
                          title: t('survey.graph.type.opinion.user'),
                          textAlign: 'center',
                          overflow: 'visible',
                          template: ({ owner }) =>
                            renderToStaticMarkup(<UserColumn t={t} user={owner} />),
                        },
                        {
                          field: 'opinion',
                          title: t('survey.graph.type.opinion'),
                          textAlign: 'center',
                          width: 250,
                        },
                      ]}
                      id="opinion"
                      dataSourceURL={
                      `/table/survey/question/${questionsStats.questions[questionSelected]?.question._id}/answers`
                      }
                      dataSourceHeaders={{
                        'Accept-Version': '1.0.0',
                        'X-Gamification-API': 'Games',
                      }}
                      dataSourceParams={{
                        populate: ['owner', 'surveyContext'],
                      }}
                      query={{
                        surveyContext: lesson?.surveyContext?.surveyContext,
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          {isExternalProvider && existSurveyContext &&
            surveyJSPoll &&
            surveyJSPoll.survey &&
            surveyJSPoll.answers && (
              <SurveyJSAnalytics
                surveyJson={surveyJSPoll.survey}
                surveyResults={surveyJSPoll.answers}
                options={{ allowDynamicLayout: false }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

SurveyDisplay.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  onLessonDone: PropTypes.string.isRequired,
  sessionId: PropTypes.string,
  lesson: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    survey: PropTypes.string.isRequired,
    surveyContext: PropTypes.string.isRequired,
  }).isRequired,
  sessionTrainer: PropTypes.string,
};

SurveyDisplay.defaultProps = {
  sessionId: null,
  sessionTrainer: null,
};

export default compose(
  translate,
  withCSRF,
)(SurveyDisplay);
